import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';
import { useData } from '../../context/DataContext';
import { useAuth } from '../../context/AuthContext';
import { exportPaymentReport, exportPaymentReportExcel } from '../../utils/exportUtils';
import './Reports.css';

const ReportPayments = () => {
  const { t } = useTranslation();
  const { selectedLocation } = useOutletContext();
  const { user } = useAuth();
  const { data } = useData();

  const [periodFilter, setPeriodFilter] = useState('all');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  const [selectedStudent, setSelectedStudent] = useState('');

  const periodOptions = [
    { value: 'all', label: 'Alles' },
    { value: 'week', label: 'Afgelopen 7 dagen' },
    { value: 'month', label: 'Deze maand' },
    { value: 'quarter', label: 'Dit kwartaal' },
    { value: 'year', label: 'Dit jaar' },
    { value: 'custom', label: 'Aangepast datumbereik' }
  ];

  const isWithinPeriod = (value) => {
    if (periodFilter === 'all') return true;
    if (!value) return false;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return false;
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    if (periodFilter === 'week') {
      const startOfWeek = new Date(startOfToday);
      startOfWeek.setDate(startOfToday.getDate() - 6);
      return date >= startOfWeek;
    }
    if (periodFilter === 'month') {
      return date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth();
    }
    if (periodFilter === 'quarter') {
      const quarter = Math.floor(now.getMonth() / 3);
      const startOfQuarter = new Date(now.getFullYear(), quarter * 3, 1);
      return date >= startOfQuarter;
    }
    if (periodFilter === 'year') {
      return date.getFullYear() === now.getFullYear();
    }
    if (periodFilter === 'custom') {
      const from = customDateFrom ? new Date(customDateFrom) : null;
      const to = customDateTo ? new Date(customDateTo + 'T23:59:59') : null;
      if (from && date < from) return false;
      if (to && date > to) return false;
      return true;
    }
    return true;
  };

  const getDisplayPaymentType = (payment) => {
    const key = payment.paymentTypeKey || payment.paymentType;
    return t(`payment.${key}`, key || '-');
  };

  const getDisplaySchoolYear = (payment) => {
    return payment.schoolYearLabel || payment.schoolYear || 'Onbekend studiejaar';
  };

  const students = (data.students || []).filter((s) => {
    if (user.role !== 'admin' && user.location) {
      return s.location === user.location;
    }
    if (user.role === 'admin' && selectedLocation && selectedLocation !== 'all') {
      return s.location === selectedLocation;
    }
    return true;
  });

  const handleExportPDF = () => {
    const student = data.students.find((s) => String(s.id) === String(selectedStudent));
    if (!student) return;
    const studentPayments = data.payments.filter(
      (p) => p.studentId === student.id && isWithinPeriod(p.paymentDate || p.dueDate || p.createdAt)
    );
    exportPaymentReport(
      studentPayments,
      student,
      data.studies.flatMap((s) => s.studyYears),
      `payment_statement_${student.name.replace(/\s+/g, '_')}`
    );
  };

  const handleExportExcel = () => {
    const student = data.students.find((s) => String(s.id) === String(selectedStudent));
    if (!student) return;
    const studentPayments = data.payments.filter(
      (p) => p.studentId === student.id && isWithinPeriod(p.paymentDate || p.dueDate || p.createdAt)
    );
    exportPaymentReportExcel(
      studentPayments,
      student,
      data.studies.flatMap((s) => s.studyYears),
      `payment_statement_${student.name.replace(/\s+/g, '_')}`
    );
  };

  const student = data.students.find((s) => String(s.id) === String(selectedStudent));
  const studentPayments = student
    ? data.payments.filter(
        (p) => p.studentId === student.id && isWithinPeriod(p.paymentDate || p.dueDate || p.createdAt)
      )
    : [];

  const paymentsByYear = {};
  studentPayments.forEach((payment) => {
    const yearKey = getDisplaySchoolYear(payment);
    if (!paymentsByYear[yearKey]) paymentsByYear[yearKey] = [];
    paymentsByYear[yearKey].push(payment);
  });

  return (
    <div className="reports-page">
      <div className="page-header">
        <h1>{t('report.paymentReport', 'Betalingen Rapport')}</h1>
      </div>

      {/* Period filter */}
      <div className="filter-button-row period-filter-row">
        <span className="filter-button-label">Periode</span>
        <div className="period-filter-controls">
          <select
            value={periodFilter}
            onChange={(e) => {
              setPeriodFilter(e.target.value);
              setCustomDateFrom('');
              setCustomDateTo('');
            }}
            className="period-select"
          >
            {periodOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          {periodFilter === 'custom' && (
            <div className="custom-date-range">
              <label>Van
                <input
                  type="date"
                  value={customDateFrom}
                  onChange={(e) => setCustomDateFrom(e.target.value)}
                  className="date-input"
                />
              </label>
              <label>Tot
                <input
                  type="date"
                  value={customDateTo}
                  min={customDateFrom || undefined}
                  onChange={(e) => setCustomDateTo(e.target.value)}
                  className="date-input"
                />
              </label>
            </div>
          )}
        </div>
      </div>

      {/* Student selector */}
      <div className="report-controls">
        <div className="form-group">
          <label>{t('enrollment.student')}</label>
          <select
            value={selectedStudent}
            onChange={(e) => setSelectedStudent(e.target.value)}
          >
            <option value="">{t('common.select')}...</option>
            {students.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="export-buttons">
        <button
          className="btn-primary"
          onClick={handleExportPDF}
          disabled={!selectedStudent}
        >
          {t('report.exportPDF')}
        </button>
        <button
          className="btn-primary"
          onClick={handleExportExcel}
          disabled={!selectedStudent}
        >
          {t('report.exportExcel')}
        </button>
      </div>

      {/* Preview */}
      {student && (
        <div className="report-preview">
          <h3>{t('payment.paymentOverview')}</h3>
          {studentPayments.length === 0 ? (
            <p>{t('payment.noPayments')}</p>
          ) : (
            <div className="payment-table-container">
              {Object.entries(paymentsByYear).map(([year, payments]) => (
                <div key={year} className="study-year-section">
                  <h4>{year}</h4>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>{t('payment.type')}</th>
                        <th>{t('payment.totalAmount')}</th>
                        <th>{t('payment.paid')}</th>
                        <th>{t('payment.remaining')}</th>
                        <th>{t('common.status')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map((p) => (
                        <tr key={p.id}>
                          <td>{getDisplayPaymentType(p)}</td>
                          <td>${Number(p.amount || 0).toFixed(2)}</td>
                          <td>${Number(p.paidAmount || 0).toFixed(2)}</td>
                          <td>${(Number(p.amount || 0) - Number(p.paidAmount || 0)).toFixed(2)}</td>
                          <td>
                            <span className={`status-badge status-${p.status}`}>
                              {t(`payment.${p.status}`)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ReportPayments;
